import React, { useCallback } from 'react';
import { View, StyleSheet } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import type { RootStackParamList } from '@/navigation/RootNavigator';
import { usePaymentMethodsList } from '@/features/payments/hooks/usePaymentMethodsList';
import { usePaymentMethodActions } from '@/features/payments/hooks/usePaymentMethodActions';
import { PaymentMethodsSection } from '@/components/residents/redesign/payments';
import type { PaymentMethod } from '@/components/residents/redesign/payments';
import { ConfirmationModal } from '@/components/residents/marketplace/ConfirmationModal';

/**
 * Mobile Payment Methods Section (Account Settings)
 * Uses the unified redesign PaymentMethodsSection with interactive mode.
 */
export const MobilePaymentMethodsSection: React.FC = () => {
  const navigation = useNavigation<StackNavigationProp<RootStackParamList>>();
  const { methods, refetch } = usePaymentMethodsList();

  const {
    setAsDefault,
    deleteMethodWithConfirmation,
    showDeleteConfirm,
    confirmDelete,
    cancelDelete,
    isUpdating,
  } = usePaymentMethodActions(refetch);

  const handleSetAsDefault = useCallback((method: PaymentMethod) => {
    setAsDefault(method);
  }, [setAsDefault]);

  const handleDelete = useCallback((method: PaymentMethod) => {
    deleteMethodWithConfirmation({
      ...method,
      name: method.label,
      last4: method.lastFour,
    });
  }, [deleteMethodWithConfirmation]);

  const handleAddNew = useCallback(() => {
    navigation.navigate('ManageWallet', { initialScreen: 'add' });
  }, [navigation]);

  const handleManage = useCallback(() => {
    navigation.navigate('ManageWallet', {});
  }, [navigation]);

  return (
    <>
      <View style={styles.container}>
        <PaymentMethodsSection
          methods={methods}
          onManage={handleManage}
          onAddNew={handleAddNew}
          gridLayout
          interactive
          onSetAsDefault={handleSetAsDefault}
          onDelete={handleDelete}
          isUpdating={isUpdating}
          showEmptyState
          emptyStateTitle="No saved payment methods"
          emptyStateMessage="You'll need to add a payment method before making your next payment."
        />
      </View>

      <ConfirmationModal
        visible={showDeleteConfirm}
        message="Remove saved payment method?"
        onCancel={cancelDelete}
        onProceed={confirmDelete}
        cancelText="No, cancel"
        proceedText="Proceed"
      />
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingTop: 8,
  },
});

export default MobilePaymentMethodsSection;
